import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import styled from 'styled-components';
import { useLocale } from '../context/LocaleContext';
import { LOCALES } from '../config/locales';

const Wrapper = styled.div`
  position: relative;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(255, 255, 255, 0.1);
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const FlagImg = styled.img`
  width: 1.5rem;
  height: 1rem;
  object-fit: cover;
  border-radius: 2px;
  flex-shrink: 0;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.25rem);
  right: 0;
  min-width: 12rem;
  max-height: 20rem;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.card};
  color: ${({ theme }) => theme.colors.foreground};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  z-index: 100;
  padding: 0.25rem;
`;

const Option = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ $active, theme }) => ($active ? theme.colors.muted : 'transparent')};
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: ${({ theme }) => theme.colors.muted};
  }
`;

export default function LanguageSwitcher() {
  const { locale, getPathForLocale } = useLocale();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const handleSelect = (loc) => {
    if (loc.code === locale.code) {
      setOpen(false);
      return;
    }
    const path = getPathForLocale(loc.pathPrefix);
    setOpen(false);
    navigate(path);
  };

  const flagUrl = (code) => `https://flagcdn.com/w40/${(code || 'us').toLowerCase()}.png`;

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="listbox">
        <FlagImg src={flagUrl(locale.countryCode)} alt="" />
        <span>{locale.label}</span>
        <ChevronDown size={14} style={{ opacity: 0.8 }} />
      </Trigger>
      {open && (
        <Dropdown role="listbox">
          {LOCALES.map((loc) => (
            <Option
              key={loc.code}
              type="button"
              role="option"
              $active={loc.code === locale.code}
              onClick={() => handleSelect(loc)}
            >
              <FlagImg src={flagUrl(loc.countryCode)} alt="" />
              {loc.label}
            </Option>
          ))}
        </Dropdown>
      )}
    </Wrapper>
  );
}
